import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Link, Stack, Typography } from '@mui/material';
import { useFilter } from 'features/filters/hooks/useFilter';
import { SearchInput } from 'greener-ui-library';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { Blueprint } from 'types';
import { OrganisationFilter } from '../OrganisationFilter';

const baseNameFilter = { filterKey: 'name__contains' };
const baseClientNameFilter = { filterKey: 'client_name__contains' };

const createNameFilter = (value: string) => ({
  ...baseNameFilter,
  value,
});

const createClientNameFilter = (value: string) => ({
  ...baseClientNameFilter,
  value,
});

const conjunctionFilter = { filterKey: 'conjunction', value: 'or' };

export const BlueprintsFilterSection = () => {
  const { isMobile } = useBreakpoint();
  const { updateFilters, getFilterValues, clearFilter, clearAllFilters, getFilters } = useFilter<Blueprint>({
    isValidFilterProperty: (property) =>
      ['name', 'project_organisation__name', 'organisation__name', 'client_name', 'conjunction'].includes(property),
  });

  const searchValue = getFilterValues?.(baseNameFilter.filterKey)?.[0] ?? '';
  const [resetKey, setResetKey] = useState(0);
  const { t } = useTranslation();

  const handleSearchChange = (val: string) => {
    if (val.trim() === '') {
      handleClearSearchInput();
    } else {
      const nameFilter = createNameFilter(val);
      const clientNameFilter = createClientNameFilter(val);
      updateFilters([nameFilter, clientNameFilter, conjunctionFilter]);
    }
  };

  const handleClearSearchInput = () => {
    clearFilter([baseNameFilter.filterKey, baseClientNameFilter.filterKey, conjunctionFilter.filterKey]);
  };

  const onClearAll = () => {
    setResetKey((key) => key + 1); // force re-render to clean state of SearchInput
    clearAllFilters();
  };

  const props = {
    handleSearchChange,
    resetKey,
    searchValue,
    getFilters,
    getFilterValues,
    updateFilters,
    clearFilter,
    onClearAll,
    t,
  };

  return isMobile ? <MobileLayout {...props} /> : <DesktopLayout {...props} />;
};

const DesktopLayout = ({
  handleSearchChange,
  resetKey,
  searchValue,
  getFilters,
  getFilterValues,
  updateFilters,
  clearFilter,
  onClearAll,
  t,
}) => {
  return (
    <Grid item container columnSpacing={1.5} rowSpacing={2}>
      <Grid item xs={6} md={4} lg={3}>
        <SearchInput
          key={resetKey}
          hasStartAdornment={false}
          defaultValue={searchValue}
          onDebouncedChange={handleSearchChange}
          placeholder={t('blueprints:card.filterSection.searchBlueprint')}
          fullWidth
        />
      </Grid>

      <Grid item xs={6} md={4} lg={3}>
        <OrganisationFilter
          updateFilters={updateFilters}
          clearFilter={clearFilter}
          getFilterValues={getFilterValues}
          filterKey="organisation__name__eq"
        />
      </Grid>

      <Grid
        item
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <ClearAllButton getFilters={getFilters} onClearAll={onClearAll} />
      </Grid>
    </Grid>
  );
};

const MobileLayout = ({
  handleSearchChange,
  resetKey,
  searchValue,
  getFilters,
  getFilterValues,
  updateFilters,
  clearFilter,
  onClearAll,
  t,
}) => {
  return (
    <>
      <Grid item container xs={12} md={9} columnSpacing={1.5} rowSpacing={2}>
        <Grid item xs={12}>
          <SearchInput
            key={resetKey}
            defaultValue={searchValue}
            onDebouncedChange={handleSearchChange}
            placeholder={t('blueprints:card.filterSection.searchBlueprint')}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <OrganisationFilter
            updateFilters={updateFilters}
            clearFilter={clearFilter}
            getFilterValues={getFilterValues}
            filterKey="organisation__name__eq"
          />
        </Grid>
      </Grid>

      <Grid item xs={8}>
        <Stack direction="row" alignItems={'center'} justifyContent={'flex-start'} gap={1}>
          <ClearAllButton getFilters={getFilters} onClearAll={onClearAll} />
        </Stack>
      </Grid>
    </>
  );
};

const ClearAllButton = ({ getFilters, onClearAll }) => {
  const { t } = useTranslation();
  return (
    <Link
      data-testid="clear-filters-button"
      component={Button}
      color="secondary"
      disabled={!getFilters().length}
      onClick={onClearAll}
      variant="body2"
    >
      {t('blueprints:card.filterSection.clearAll')}
    </Link>
  );
};
